import { FC } from 'react';
import { IonItem, IonLabel } from '@ionic/react';
import Button from '../../../../Button/Button';
import Modal from '../../../../Modals/Modal/Modal';
import GenerateMatrixModalContent from '../../../../Modals/GenerateMatrixModalContent/GenerateMatrixModalContent';
import SettingsPanel from '../SettingsPanel/SettingsPanel';
import { useEditorPanelHandlers } from '../../hooks/useEditorPanelHandlers';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { useDatasetContext } from '../../../../../hooks/context/useDatasetContext';
import { useLayoutEditorContext } from '../../../../../hooks/context/useLayoutEditorContext';
import {
  FullscreenReferenceType,
  JSONObject,
} from '../../../../../types/types';

interface EditorCanvasFormProps {
  onOpenFullscreenReference: (reference: FullscreenReferenceType) => void;
}

// Content of the "Plátno" accordion section (header + content slots, no
// accordion tag): the definition-driven canvas form + the add object /
// generate matrix actions. Everything context-derived (incl. the glue
// handlers) is consumed here; the matrix dialog is owned here too — the
// component that opens the modal also renders it.
const EditorCanvasForm: FC<EditorCanvasFormProps> = ({
  onOpenFullscreenReference,
}) => {
  const { t } = useTranslate();
  // Page-wide reference dataset cache (shared with the fullscreen reference route).
  const { referenceDatasets, setReferenceDatasets } = useDatasetContext();
  const {
    definition,
    canvas,
    isFixedMode,
    isMatrixSupported,
    isMatrixDialogOpen,
    closeMatrixDialog,
    saveErrors,
  } = useLayoutEditorContext();
  const {
    handleAddObject,
    handleOpenMatrixModal,
    handleCanvasFieldChange,
    handleCanvasFieldDefault,
  } = useEditorPanelHandlers();

  const isAddDisabled = !!canvas.lockItems;
  const isMatrixDisabled = !!canvas.lockItems || isFixedMode;

  // Save-validation errors rendered under the offending inputs.
  const getCanvasFieldSaveError = (name: string): string | null =>
    saveErrors.find(
      (saveError) => saveError.scope === 'canvas' && saveError.field === name
    )?.errorKey ?? null;

  if (!definition?.canvasForm) {
    return null;
  }

  return (
    <>
      <IonItem
        className="layout-editor-panel-section-header"
        slot="header"
        lines="none"
      >
        <IonLabel>{t('layoutEditor.canvasSection')}</IonLabel>
      </IonItem>
      <div slot="content">
        <SettingsPanel
          form={definition.canvasForm}
          data={canvas as unknown as JSONObject}
          referenceDatasets={referenceDatasets}
          setReferenceDatasets={setReferenceDatasets}
          getFieldSaveError={getCanvasFieldSaveError}
          handlerScope="canvas"
          onFieldChange={handleCanvasFieldChange}
          onFieldDefault={handleCanvasFieldDefault}
          onOpenFullscreenReference={onOpenFullscreenReference}
        />
        <div className="layout-editor-panel-actions">
          <Button
            fullWidth
            className="layout-editor-panel-action"
            disabled={isAddDisabled}
            onClick={handleAddObject}
          >
            {t('layoutEditor.addObject')}
          </Button>
          {isMatrixSupported && (
            <Button
              fullWidth
              className="layout-editor-panel-action"
              disabled={isMatrixDisabled}
              onClick={handleOpenMatrixModal}
            >
              {t('layoutEditor.generateMatrix')}
            </Button>
          )}
        </div>
        {/* Controlled by the context so the dialog reopens after the
            fullscreen reference route round-trip. */}
        {isMatrixDialogOpen && (
          <Modal
            isOpen
            isUnclosable={false}
            onClose={closeMatrixDialog}
            content={<GenerateMatrixModalContent />}
          />
        )}
      </div>
    </>
  );
};

export default EditorCanvasForm;
